import React, { Suspense, useState, useRef, useEffect } from 'react';
import { Canvas, useLoader, useThree } from '@react-three/fiber';
import {
  OrbitControls,
  Environment,
  Center,
  AccumulativeShadows,
  RandomizedLight,
} from '@react-three/drei';
import Ring from './Ring';
import * as THREE from 'three';
import './style.css';
import { RGBELoader } from 'three-stdlib';
import {
  EffectComposer,
  Bloom,
  N8AO,
} from '@react-three/postprocessing';
import LottieLoader from './LottieLoader';
import { useMediaQuery } from 'react-responsive';

export default function App() {
  const [showOverlay, setShowOverlay] = useState(false);
  const interactionTimeout = useRef(null);
  const canvasRef = useRef();

  const resetInteractionTimer = () => {
    if (interactionTimeout.current) {
      clearTimeout(interactionTimeout.current);
    }
    setShowOverlay(false);
    interactionTimeout.current = setTimeout(() => {
      setShowOverlay(true);
    }, 2000);
  };

  useEffect(() => {
    const canvasElement = canvasRef.current;
    canvasElement.addEventListener('mousemove', resetInteractionTimer);
    canvasElement.addEventListener('touchstart', resetInteractionTimer);
    canvasElement.addEventListener('touchmove', resetInteractionTimer);

    resetInteractionTimer();

    return () => {
      canvasElement.removeEventListener('mousemove', resetInteractionTimer);
      canvasElement.removeEventListener('touchstart', resetInteractionTimer);
      canvasElement.removeEventListener('touchmove', resetInteractionTimer);
      clearTimeout(interactionTimeout.current);
    };
  }, []);

  // State for environment map rotation around Y-axis
  // Set initial rotation to 90 degrees (Math.PI / 2 radians)
  const [envRotationY, setEnvRotationY] = useState(Math.PI / 2);

  const controlsRef = useRef();

  return (
    <div className="viewer-container" ref={canvasRef}>
      <h1 className="viewer-heading">
        Transform Your Online Store With Cela 3D Visualization
      </h1>
      <p className="viewer-subheading">
        Interactive, lifelike product displays to increase engagement and sales
      </p>

      <div className="canvas-container">
        {showOverlay && (
          <img
            src="hand.gif"
            alt="Inactivity Overlay"
            className="inactivity-overlay"
          />
        )}
        <Suspense fallback={<LottieLoader />}>
          <Canvas
            shadows
            gl={{ antialias: true }}
            camera={{ position: [-5, 5, 14], fov: 20 }}
          >
            <Model envRotationY={envRotationY} />
            <OrbitControls
              ref={controlsRef}
              enablePan={false}
              enableZoom={true}
              makeDefault
            />
            <EnvironmentControls
              controlsRef={controlsRef}
              setEnvRotationY={setEnvRotationY}
            />
            <EffectComposer>
              <N8AO aoRadius={0.15} intensity={4} distanceFalloff={2} />
              <Bloom
                luminanceThreshold={3.5}
                intensity={0.85}
                levels={9}
                mipmapBlur
              />
            </EffectComposer>
          </Canvas>
        </Suspense>
      </div>

      <a
        href="https://calendly.com/vincent-119"
        target="_blank"
        rel="noopener noreferrer"
        className="viewer-demo-button"
      >
        Book a Demo
      </a>
    </div>
  );
}

// EnvironmentControls Component
function EnvironmentControls({ controlsRef, setEnvRotationY }) {
  const { gl } = useThree();

  const isDragging = useRef(false);
  const prevMousePosition = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (event.shiftKey && event.button === 0) {
        // Left-click with Shift
        isDragging.current = true;
        prevMousePosition.current = { x: event.clientX, y: event.clientY };
        if (controlsRef.current) controlsRef.current.enabled = false; // Disable OrbitControls
      }
    };

    const handlePointerMove = (event) => {
      if (isDragging.current) {
        const deltaX = event.clientX - prevMousePosition.current.x;

        // Update environment rotation Y
        setEnvRotationY((prevRotationY) => prevRotationY + deltaX * 0.005);

        prevMousePosition.current = { x: event.clientX, y: event.clientY };
      }
    };

    const handlePointerUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        if (controlsRef.current) controlsRef.current.enabled = true; // Re-enable OrbitControls
      }
    };

    gl.domElement.addEventListener('pointerdown', handlePointerDown);
    gl.domElement.addEventListener('pointermove', handlePointerMove);
    gl.domElement.addEventListener('pointerup', handlePointerUp);

    // Prevent context menu from appearing on right-click
    const preventContextMenu = (e) => e.preventDefault();
    gl.domElement.addEventListener('contextmenu', preventContextMenu);

    return () => {
      gl.domElement.removeEventListener('pointerdown', handlePointerDown);
      gl.domElement.removeEventListener('pointermove', handlePointerMove);
      gl.domElement.removeEventListener('pointerup', handlePointerUp);
      gl.domElement.removeEventListener('contextmenu', preventContextMenu);
    };
  }, [gl, controlsRef, setEnvRotationY]);

  return null;
}

// In Viewer.js or where the Model component is defined

function Model({ envRotationY }) {
  const [environmentMap, setEnvironmentMap] = useState(null);
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  const scale = isMobile ? 0.02 : 0.06;
  const { gl, scene } = useThree();

  useEffect(() => {
    const pmremGenerator = new THREE.PMREMGenerator(gl);
    pmremGenerator.compileEquirectangularShader();

    new RGBELoader()
      .loadAsync('/cyclorama_hard_light_2k.hdr')
      .then((texture) => {
        const mapTexture = pmremGenerator.fromEquirectangular(texture).texture;
        setEnvironmentMap(mapTexture);

        // Set the environment map for PBR materials in the scene
        scene.environment = mapTexture;

        texture.dispose();
        pmremGenerator.dispose();
      })
      .catch((error) => console.error('Failed to load textures:', error));
  }, [gl, scene]);

  // Set the background color to white
  useEffect(() => {
    scene.background = new THREE.Color(0xffffff);
  }, [scene]);

  if (!environmentMap) {
    return null;
  }

  return (
    <>
      {/* Use the Environment component without displaying it in the background */}
      <Environment
        background={false}
        map={environmentMap}
        rotation={[0, envRotationY, 0]}
      />

      <group position={[0, -0.1, 0]}>
        <Center top position={[0, 0, 0]} rotation={[-0.0, 0, 0.0]}>
          <Ring
            map={environmentMap}
            envRotationY={envRotationY}
            scale={scale}
            environmentMap={environmentMap}
          />
        </Center>
        <AccumulativeShadows temporal frames={60} opacity={0.2}>
          <RandomizedLight
            radius={4}
            amount={4}
            intensity={1.0}
            position={[0, 5, 0]}
          />
        </AccumulativeShadows>
      </group>
    </>
  );
}

